import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { ResourceGrantListItem, DeletionRequestListItem, GrantedResource } from "@workspace/api-client-react";

export type { ResourceGrantListItem, DeletionRequestListItem, GrantedResource };

/**
 * The grant resource endpoint is a discriminated resource document. Keep the
 * locally-authored extension permissive while generated clients roll forward:
 * resource-specific editable properties are returned alongside the common
 * envelope and PATCH accepts only those properties.
 */
export type GrantedResourceDocument = GrantedResource & Record<string, unknown> & {
  fields?: Record<string, unknown>;
  data?: Record<string, unknown>;
};

export type GrantedResourcePatch = Record<string, unknown>;

export function useSentResourceGrants() {
  return useQuery<ResourceGrantListItem[]>({
    queryKey: ["/api/resource-grants/sent"],
  });
}

export function useReceivedResourceGrants() {
  return useQuery<ResourceGrantListItem[]>({
    queryKey: ["/api/resource-grants/received"],
  });
}

export function useGetGrantedResource(grantId: string | null) {
  return useQuery<GrantedResourceDocument>({
    queryKey: ["/api/resource-grants", grantId, "resource"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/resource-grants/${grantId}/resource`);
      return res.json();
    },
    enabled: !!grantId
  });
}

export function useUpdateGrantedResource() {
  return useMutation({
    mutationFn: async (data: { grantId: string; patch?: GrantedResourcePatch; title?: string; summary?: string | null }) => {
      const { grantId, patch, ...legacyPatch } = data;
      const res = await apiRequest("PATCH", `/api/resource-grants/${grantId}/resource`, patch ?? legacyPatch);
      return res.json();
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/resource-grants", vars.grantId, "resource"] });
      queryClient.invalidateQueries({ queryKey: ["/api/resource-grants/received"] });
    },
  });
}

export function useCreateResourceGrant() {
  return useMutation({
    mutationFn: async (data: { recipientUserId: string; resourceType: string; resourceId: string; permission: string }) => {
      const res = await apiRequest("POST", "/api/resource-grants", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/resource-grants/sent"] });
    },
  });
}

export function useUpdateResourceGrant() {
  return useMutation({
    mutationFn: async (data: { id: string; permission: string }) => {
      const res = await apiRequest("PATCH", `/api/resource-grants/${data.id}`, { permission: data.permission });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/resource-grants/sent"] });
      queryClient.invalidateQueries({ queryKey: ["/api/resource-grants/received"] });
    },
  });
}

export function useRevokeResourceGrant() {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/resource-grants/${id}`);
      if (res.status === 204) return undefined;
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/resource-grants/sent"] });
      queryClient.invalidateQueries({ queryKey: ["/api/resource-grants/received"] });
      queryClient.invalidateQueries({ queryKey: ["/api/deletion-requests"] });
    },
  });
}

export function useDeletionRequests() {
  return useQuery<DeletionRequestListItem[]>({
    queryKey: ["/api/deletion-requests"],
  });
}

export function useCreateDeletionRequest() {
  return useMutation({
    mutationFn: async (data: { grantId: string; reason?: string }) => {
      const res = await apiRequest("POST", `/api/resource-grants/${data.grantId}/deletion-requests`, { reason: data.reason });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/deletion-requests"] });
    },
  });
}

export function useDecideDeletionRequest() {
  return useMutation({
    mutationFn: async (data: { id: string; decision: "approve" | "reject"; reason?: string }) => {
      const res = await apiRequest("POST", `/api/deletion-requests/${data.id}/decision/${data.decision}`, { reason: data.reason });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/deletion-requests"] });
    },
  });
}

export function useExecuteDeletionRequest() {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/deletion-requests/${id}/resource`);
      if (res.status === 204) return undefined;
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/deletion-requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/resource-grants/sent"] });
      queryClient.invalidateQueries({ queryKey: ["/api/resource-grants/received"] });
      queryClient.invalidateQueries({ queryKey: ["/api/resource-grants"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
    },
  });
}
